import React, { useMemo, useState } from 'react';
import { Autocomplete, IconButton, InputAdornment, TableCell, TableRow, TextField, Tooltip } from '@mui/material';
import { FilterAltOff as FilterAltOffIcon, Search as SearchIcon } from '@mui/icons-material';

// Paging, a per-column filter row and a search box: what every table grid in
// this app has. useTableControls does all three in the browser, for a list
// the API returns whole (e.g. SimpleLookupManagement's lookup screens). A
// server-paged grid keeps its own state but can still render
// TableSearchField and TableFilterRow. See the add-table-grid skill.

export interface TableColumn<T> {
  key: string;
  label: string;
  // The column's displayed text: what the search, the column filter and the
  // filter's option list match against.
  getValue: (item: T) => string;
  // 'text' (default): contains, ignoring case. 'select': one of the column's
  // distinct values, for reference/yes-no columns. 'none': no filter box.
  filter?: 'text' | 'select' | 'none';
  // A text filter's placeholder, when "Filter" doesn't say enough (e.g. a
  // number column's ">5, 2-4").
  placeholder?: string;
  // Replaces a text filter's "contains", e.g. for that number column.
  matches?: (item: T, wanted: string) => boolean;
}

// What TableFilterRow needs. useTableControls returns it; a server-paged
// grid builds the same shape from its own state.
export interface TableFilterState {
  filters: Record<string, string>;
  setFilter: (key: string, value: string) => void;
  // Options for each 'select' column.
  filterOptions: Record<string, string[]>;
  hasActiveFilters: boolean;
  clearFilters: () => void;
}

export interface TablePaginationState {
  count: number;
  page: number; // 0-indexed, as MUI TablePagination
  rowsPerPage: number;
  rowsPerPageOptions: number[];
  onPageChange: (event: unknown, page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export interface TableControls<T> extends TableFilterState {
  search: string;
  setSearch: (value: string) => void;
  // Every item passing the search and filters, and the current page of them.
  filteredItems: T[];
  pageItems: T[];
  // Spread onto <TablePagination component="div" {...paginationProps} />.
  paginationProps: TablePaginationState;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// `columns` must be stable between renders (module-level, or useMemo).
export function useTableControls<T>(items: T[], columns: TableColumn<T>[], defaultPageSize = 25): TableControls<T> {
  const [search, setSearchState] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const setSearch = (value: string) => {
    setSearchState(value);
    setPage(0);
  };
  const setFilter = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };
  const clearFilters = () => {
    setFilters({});
    setPage(0);
  };

  const { filteredItems, filterOptions } = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matchesSearch = (item: T) => !term || columns.some((c) => c.getValue(item).toLowerCase().includes(term));
    const active = columns.filter((c) => c.filter !== 'none' && filters[c.key]?.trim());
    const matchesColumn = (item: T, c: TableColumn<T>) => {
      const value = c.getValue(item);
      const wanted = filters[c.key].trim();
      if (c.matches) return c.matches(item, wanted);
      return c.filter === 'select' ? value === wanted : value.toLowerCase().includes(wanted.toLowerCase());
    };

    const searched = items.filter(matchesSearch);
    const filtered = searched.filter((item) => active.every((c) => matchesColumn(item, c)));

    // Each select column offers the values left by the search and the
    // *other* filters, so e.g. picking a Business narrows the Farm list.
    // The current pick stays in the list even when the others rule it out.
    const options: Record<string, string[]> = {};
    columns
      .filter((c) => c.filter === 'select')
      .forEach((c) => {
        const others = active.filter((o) => o.key !== c.key);
        const values = new Set<string>();
        searched.forEach((item) => {
          if (others.every((o) => matchesColumn(item, o))) {
            const value = c.getValue(item);
            if (value) values.add(value);
          }
        });
        if (filters[c.key]) values.add(filters[c.key]);
        options[c.key] = Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      });

    return { filteredItems: filtered, filterOptions: options };
  }, [items, columns, search, filters]);

  // A delete or a narrower filter can leave the page past the end.
  const lastPage = Math.max(0, Math.ceil(filteredItems.length / pageSize) - 1);
  const currentPage = Math.min(page, lastPage);
  const pageItems = filteredItems.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  return {
    search,
    setSearch,
    filters,
    setFilter,
    filterOptions,
    hasActiveFilters: Object.values(filters).some((v) => v.trim()),
    clearFilters,
    filteredItems,
    pageItems,
    paginationProps: {
      count: filteredItems.length,
      page: currentPage,
      rowsPerPage: pageSize,
      rowsPerPageOptions: PAGE_SIZE_OPTIONS,
      onPageChange: (_, newPage) => setPage(newPage),
      onRowsPerPageChange: (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setPage(0);
      },
    },
  };
}

export const TableSearchField: React.FC<{ value: string; onChange: (value: string) => void; placeholder?: string }> = ({
  value,
  onChange,
  placeholder = 'Search…',
}) => (
  <TextField
    size="small"
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon fontSize="small" />
        </InputAdornment>
      ),
    }}
  />
);

interface TableFilterRowProps<T> {
  columns: TableColumn<T>[];
  state: TableFilterState;
  // Header cells before/after the filterable columns (a checkbox column,
  // Status, Actions...). The Clear filters button goes in the last one.
  leadingCells?: number;
  trailingCells?: number;
}

// Second row of a <TableHead>, under the column labels.
export function TableFilterRow<T>({ columns, state, leadingCells = 0, trailingCells = 1 }: TableFilterRowProps<T>) {
  return (
    <TableRow sx={{ '& .MuiTableCell-root': { pt: 0, verticalAlign: 'top' } }}>
      {Array.from({ length: leadingCells }, (_, i) => (
        <TableCell key={`lead-${i}`} />
      ))}
      {columns.map((c) => (
        <TableCell key={c.key}>
          {c.filter === 'none' ? null : c.filter === 'select' ? (
            <Autocomplete
              size="small"
              options={state.filterOptions[c.key] ?? []}
              value={state.filters[c.key] || null}
              onChange={(_, value) => state.setFilter(c.key, value ?? '')}
              renderInput={(params) => <TextField {...params} placeholder="All" />}
              sx={{ minWidth: 140 }}
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              placeholder={c.placeholder ?? 'Filter'}
              value={state.filters[c.key] ?? ''}
              onChange={(e) => state.setFilter(c.key, e.target.value)}
              sx={{ minWidth: 100 }}
            />
          )}
        </TableCell>
      ))}
      {Array.from({ length: Math.max(0, trailingCells - 1) }, (_, i) => (
        <TableCell key={`trail-${i}`} />
      ))}
      {trailingCells > 0 && (
        <TableCell align="right">
          {state.hasActiveFilters && (
            <Tooltip title="Clear filters">
              <IconButton size="small" onClick={state.clearFilters}>
                <FilterAltOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
